import React, { useState, useEffect, useCallback, useReducer } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import { exportBinToPDF, exportBinToExcel, exportAllBinsToPDF, exportAllBinsToExcel } from '../utils/exportUtils'
import {
  ArchiveBoxIcon,
  QrCodeIcon,
  PhotoIcon,
  DocumentArrowDownIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

type StorageBin = Database['public']['Tables']['storage_bins']['Row'] & {
  cookie_cutters?: Database['public']['Tables']['cookie_cutters']['Row'][]
  qr_codes?: Database['public']['Tables']['qr_codes']['Row'][]
  cutter_count?: number
}

const BINS_PAGE_SIZE = 10
const CUTTERS_PAGE_SIZE = 10

// Editing state consolidated
interface EditingState {
  binId: string | null
  name: string
  location: string
}

type EditingAction =
  | { type: 'START_EDITING'; payload: { binId: string; name: string; location: string } }
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_LOCATION'; payload: string }
  | { type: 'CANCEL' }

const editingInitialState: EditingState = {
  binId: null,
  name: '',
  location: '',
}

function editingReducer(state: EditingState, action: EditingAction): EditingState {
  switch (action.type) {
    case 'START_EDITING':
      return { binId: action.payload.binId, name: action.payload.name, location: action.payload.location }
    case 'SET_NAME':
      return { ...state, name: action.payload }
    case 'SET_LOCATION':
      return { ...state, location: action.payload }
    case 'CANCEL':
      return editingInitialState
    default:
      return state
  }
}

export default function StorageBins() {
  const { user } = useAuth()
  const [storageBins, setStorageBins] = useState<StorageBin[]>([])
  const [totalBins, setTotalBins] = useState(0)
  const [binsPage, setBinsPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedBin, setSelectedBin] = useState<StorageBin | null>(null)
  const [selectedBinCutters, setSelectedBinCutters] = useState<Database['public']['Tables']['cookie_cutters']['Row'][]>([])
  const [totalCutters, setTotalCutters] = useState(0)
  const [cuttersPage, setCuttersPage] = useState(0)
  const [cuttersLoading, setCuttersLoading] = useState(false)

  const [editing, dispatchEditing] = useReducer(editingReducer, editingInitialState)

  const fetchStorageBins = useCallback(async () => {
    if (!user) return

    setLoading(true)
    try {
      // Get total count first
      const { count } = await supabase
        .from('storage_bins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)

      setTotalBins(count || 0)

      // Fetch paginated bins with count of cutters (not full cutter data)
      const from = binsPage * BINS_PAGE_SIZE
      const to = from + BINS_PAGE_SIZE - 1

      const { data, error } = await supabase
        .from('storage_bins')
        .select(`
          *,
          qr_codes (*),
          cookie_cutters (id)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to)

      if (!error && data) {
        // Transform data to include cutter count instead of full array
        const binsWithCount = data.map((bin: any) => ({
          ...bin,
          cutter_count: bin.cookie_cutters?.length || 0,
          cookie_cutters: undefined, // Don't store full array
        }))
        setStorageBins(binsWithCount)
      }
    } catch {
      setStorageBins([])
      setTotalBins(0)
    } finally {
      setLoading(false)
    }
  }, [user, binsPage])

  // Fetch cutters for selected bin with pagination
  const fetchBinCutters = useCallback(async (binId: string, page: number = cuttersPage) => {
    if (!user) return

    setCuttersLoading(true)
    try {
      // Get total count
      const { count } = await supabase
        .from('cookie_cutters')
        .select('*', { count: 'exact', head: true })
        .eq('bin_id', binId)
        .eq('user_id', user.id)

      setTotalCutters(count || 0)

      // Fetch paginated cutters
      const from = page * CUTTERS_PAGE_SIZE
      const to = from + CUTTERS_PAGE_SIZE - 1

      const { data, error } = await supabase
        .from('cookie_cutters')
        .select('*')
        .eq('bin_id', binId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to)

      if (!error) {
        setSelectedBinCutters(data || [])
      }
    } catch {
      setSelectedBinCutters([])
      setTotalCutters(0)
    } finally {
      setCuttersLoading(false)
    }
  }, [user, cuttersPage])

  useEffect(() => {
    fetchStorageBins()
  }, [fetchStorageBins])

  useEffect(() => {
    if (selectedBin) {
      fetchBinCutters(selectedBin.id)
    }
  }, [selectedBin, fetchBinCutters])

  const handleSelectBin = (bin: StorageBin) => {
    setSelectedBin(bin)
    setCuttersPage(0)
    // Fetch immediately with page 0 to avoid stale closure
    fetchBinCutters(bin.id, 0)
  }

  const exportBinReport = (bin: StorageBin, format: 'pdf' | 'excel' = 'pdf') => {
    try {
      // Need to fetch all cutters for export
      const exportBin = { ...bin, cookie_cutters: selectedBinCutters }
      if (format === 'pdf') {
        exportBinToPDF(exportBin)
      } else {
        exportBinToExcel(exportBin)
      }
    } catch {
      window.alert('Failed to export report. Please try again.')
    }
  }

  const exportAllBinsReport = async (format: 'pdf' | 'excel' = 'pdf') => {
    try {
      // Fetch all bins with all cutters for export
      const { data } = await supabase
        .from('storage_bins')
        .select(`
          *,
          cookie_cutters (*),
          qr_codes (*)
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })

      if (data) {
        if (format === 'pdf') {
          exportAllBinsToPDF(data)
        } else {
          exportAllBinsToExcel(data)
        }
      }
    } catch {
      window.alert('Failed to export report. Please try again.')
    }
  }

  const startEditing = (bin: StorageBin) => {
    dispatchEditing({
      type: 'START_EDITING',
      payload: { binId: bin.id, name: bin.name, location: bin.location || '' }
    })
  }

  const cancelEditing = () => {
    dispatchEditing({ type: 'CANCEL' })
  }

  const updateBinName = async (binId: string) => {
    if (!editing.name.trim()) {
      window.alert('Please enter a valid name')
      return
    }

    if (!user) {
      window.alert('User not authenticated')
      return
    }

    try {
      const { error } = await supabase
        .from('storage_bins')
        .update({
          name: editing.name.trim(),
          location: editing.location.trim() || null
        })
        .eq('id', binId)
        .eq('user_id', user.id)

      if (error) {
        window.alert('Failed to update storage bin name')
        return
      }

      // Update local state
      setStorageBins(prevBins =>
        prevBins.map(bin =>
          bin.id === binId ? {
            ...bin,
            name: editing.name.trim(),
            location: editing.location.trim() || null
          } : bin
        )
      )

      // Update selected bin if it's the one being edited
      if (selectedBin?.id === binId) {
        setSelectedBin(prev => prev ? {
          ...prev,
          name: editing.name.trim(),
          location: editing.location.trim() || null
        } : null)
      }

      dispatchEditing({ type: 'CANCEL' })
    } catch {
      window.alert('Failed to update storage bin name')
    }
  }

  const totalBinsPages = totalBins > 0 ? Math.ceil(totalBins / BINS_PAGE_SIZE) : 1
  const canGoPreviousBins = binsPage > 0
  const canGoNextBins = binsPage < totalBinsPages - 1

  const totalCuttersPages = totalCutters > 0 ? Math.ceil(totalCutters / CUTTERS_PAGE_SIZE) : 1
  const canGoPreviousCutters = cuttersPage > 0
  const canGoNextCutters = cuttersPage < totalCuttersPages - 1

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Storage Bins</h1>
          <p className="mt-1 text-sm text-gray-700">
            Organize your cookie cutters by storage location
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => exportAllBinsReport('pdf')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export PDF
          </button>
          <button
            onClick={() => exportAllBinsReport('excel')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-700">Loading storage bins...</div>
        </div>
      ) : storageBins.length === 0 && binsPage === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No storage bins</h3>
          <p className="mt-1 text-sm text-gray-700">
            Create storage bins in the mobile app to organize your cookie cutters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Storage Bins List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Your Storage Bins</h2>
              {totalBins > 0 && (
                <span className="text-sm text-gray-500">{totalBins} total</span>
              )}
            </div>
            {storageBins.map((bin) => (
              <div
                key={bin.id}
                className={`group bg-white shadow rounded-lg p-6 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  selectedBin?.id === bin.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => handleSelectBin(bin)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSelectBin(bin)
                  }
                }}
                role="button"
                aria-pressed={selectedBin?.id === bin.id}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <ArchiveBoxIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      {editing.binId === bin.id ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editing.name}
                              onChange={(e) => dispatchEditing({ type: 'SET_NAME', payload: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateBinName(bin.id)
                                } else if (e.key === 'Escape') {
                                  cancelEditing()
                                }
                              }}
                              className="flex-1 px-2 py-1 text-lg font-medium text-gray-900 bg-white border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Storage bin name"
                              autoFocus
                            />
                            <button
                              onClick={() => updateBinName(bin.id)}
                              className="p-1 text-green-600 hover:text-green-700"
                              title="Save"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={editing.location}
                            onChange={(e) => dispatchEditing({ type: 'SET_LOCATION', payload: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateBinName(bin.id)
                              } else if (e.key === 'Escape') {
                                cancelEditing()
                              }
                            }}
                            className="w-full px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Location (optional)"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{bin.name}</h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              startEditing(bin)
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit name"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {bin.description && (
                        <p className="text-sm text-gray-700">{bin.description}</p>
                      )}
                      {bin.location && (
                        <p className="text-sm text-blue-600 flex items-center mt-1">
                          <span className="mr-1">📍</span>
                          {bin.location}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {bin.qr_codes && bin.qr_codes.length > 0 && (
                      <QrCodeIcon className="h-5 w-5 text-blue-500" title="Has QR Code" />
                    )}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {bin.cutter_count || 0} cutters
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-700">
                    Created {new Date(bin.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        exportBinReport(bin, 'pdf')
                      }}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      PDF
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        exportBinReport(bin, 'excel')
                      }}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Excel
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Bins Pagination */}
            {totalBinsPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <button
                  onClick={() => setBinsPage(p => p - 1)}
                  disabled={!canGoPreviousBins}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {binsPage + 1} of {totalBinsPages}
                </span>
                <button
                  onClick={() => setBinsPage(p => p + 1)}
                  disabled={!canGoNextBins}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            )}
          </div>

          {/* Bin Details */}
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">
              {selectedBin ? `${selectedBin.name} Contents` : 'Select a Bin'}
            </h2>

            {selectedBin ? (
              <div className="bg-white shadow rounded-lg">
                {/* Bin Info */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {editing.binId === selectedBin.id ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editing.name}
                              onChange={(e) => dispatchEditing({ type: 'SET_NAME', payload: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateBinName(selectedBin.id)
                                } else if (e.key === 'Escape') {
                                  cancelEditing()
                                }
                              }}
                              className="flex-1 px-2 py-1 text-lg font-medium text-gray-900 bg-white border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Storage bin name"
                              autoFocus
                            />
                            <button
                              onClick={() => updateBinName(selectedBin.id)}
                              className="p-1 text-green-600 hover:text-green-700"
                              title="Save"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={editing.location}
                            onChange={(e) => dispatchEditing({ type: 'SET_LOCATION', payload: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateBinName(selectedBin.id)
                              } else if (e.key === 'Escape') {
                                cancelEditing()
                              }
                            }}
                            className="w-full px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Location (optional)"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{selectedBin.name}</h3>
                          <button
                            onClick={() => startEditing(selectedBin)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Edit name"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {selectedBin.description && (
                        <p className="text-sm text-gray-700 mt-1">{selectedBin.description}</p>
                      )}
                      {selectedBin.location && (
                        <p className="text-sm text-blue-600 flex items-center mt-1">
                          <span className="mr-1">📍</span>
                          {selectedBin.location}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-700">
                        {totalCutters} cookie cutters
                      </div>
                      {selectedBin.qr_codes && selectedBin.qr_codes.length > 0 && (
                        <div className="text-sm text-blue-600 flex items-center mt-1">
                          <QrCodeIcon className="h-4 w-4 mr-1" />
                          QR Code Available
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cookie Cutters */}
                <div className="p-6">
                  {cuttersLoading ? (
                    <div className="text-center py-8">
                      <div className="text-gray-700">Loading cutters...</div>
                    </div>
                  ) : selectedBinCutters.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {selectedBinCutters.map((cutter) => (
                          <div key={cutter.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                            <div className="flex-shrink-0">
                              {cutter.photo_url ? (
                                <img
                                  className="h-12 w-12 rounded-lg object-cover"
                                  src={cutter.photo_url}
                                  alt={cutter.name}
                                  loading="lazy"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                  <PhotoIcon className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {cutter.name}
                              </p>
                              {cutter.category && (
                                <p className="text-xs text-blue-600">{cutter.category}</p>
                              )}
                              {cutter.description && (
                                <p className="text-xs text-gray-700 truncate">{cutter.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Cutters Pagination */}
                      {totalCuttersPages > 1 && (
                        <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                          <button
                            onClick={() => setCuttersPage(p => p - 1)}
                            disabled={!canGoPreviousCutters}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            Previous
                          </button>
                          <span className="text-sm text-gray-700">
                            Page {cuttersPage + 1} of {totalCuttersPages}
                          </span>
                          <button
                            onClick={() => setCuttersPage(p => p + 1)}
                            disabled={!canGoNextCutters}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No cookie cutters</h3>
                      <p className="mt-1 text-sm text-gray-700">
                        This storage bin doesn't contain any cookie cutters yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center py-8">
                  <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a storage bin</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Click on a storage bin to view its contents and details.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
